import React, { Dispatch, useRef, useState } from 'react';
import cn from 'classnames';
import Panel from '../Panel';
import CopySection, { FinishStepButton } from '../CopySection';
import validEmail from '@support/valid-email';
import { CheckIcon, PlusIcon, XIcon } from '@components/Icons';
import Tooltip, { Overlay } from '@components/Tooltip';
import styles from './InviteFriendsPanel.module.scss';
import { apiRequest } from '@support/api';

// could use useContactSearch for closest people, pre-populate list with first 5 that have email addresses?
const EmailList = ({
  inviteList,
  setInviteList,
}: {
  inviteList: string[];
  setInviteList: Dispatch<string[]>;
}) => {
  const [addingEmail, setAddingEmail] = React.useState(true);
  const [newEmail, setNewEmail] = React.useState('');
  const addEmailRowRef = useRef<HTMLLIElement>(null);

  const addEmail = () => {
    setInviteList([...inviteList, newEmail]);
    setNewEmail('');
  };

  const cancelAdding = () => {
    setAddingEmail(false);
    setNewEmail('');
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && validEmail(newEmail)) {
      addEmail();
    }

    if (e.key === 'Escape' && newEmail.length !== 0) {
      e.stopPropagation();
      setNewEmail('');
    }

    if (e.key === 'Escape' && newEmail.length === 0) {
      e.stopPropagation();
      setAddingEmail(false);
    }
  };

  return (
    <div className={cn(styles.container, styles.emailList)}>
      <ul className={styles.options}>
        {inviteList.map((email, idx) => (
          <li key={idx}>
            {email}
            <button
              className={styles.btn}
              onClick={() => setInviteList(inviteList.filter((_, i) => i !== idx))}
            >
              <XIcon width={12} height={12} hover hoverOpacity />
            </button>
          </li>
        ))}
        {(addingEmail || inviteList.length === 0) && (
          <li ref={addEmailRowRef} className="addMemberRow">
            <input
              type="email"
              className={styles.emailInput}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder="Enter email address"
              autoFocus
            />
            <div className={styles.buttons}>
              <Tooltip
                placement="top"
                overlay={
                  <Overlay className={cn(validEmail(newEmail) && styles.hidden)}>
                    Please enter a valid email address
                  </Overlay>
                }
              >
                <span>
                  <button
                    onClick={addEmail}
                    className={styles.btn}
                    disabled={!validEmail(newEmail)}
                  >
                    <CheckIcon width={12} height={12} />
                  </button>
                </span>
              </Tooltip>
              {inviteList.length > 0 && (
                <button onClick={cancelAdding} className={styles.btn}>
                  <XIcon width={12} height={12} hover hoverOpacity />
                </button>
              )}
            </div>
          </li>
        )}
      </ul>
      <button
        className={styles.addEmailBtn}
        onClick={() => setAddingEmail(true)}
        disabled={addingEmail || inviteList.length === 0}
      >
        <PlusIcon width="10" height="10" />
        Add Member
      </button>
    </div>
  );
};

export default function InviteFriendsPanel({
  proceedToNextStep,
}: {
  proceedToNextStep: () => void;
}) {
  const [inviteList, setInviteList] = useState<string[]>([]);
  const [sendingInvites, setSendingInvites] = useState(false);

  const finishStep = () => {
    if (inviteList.length === 0) {
      proceedToNextStep();
      return;
    }

    // send invites
    setSendingInvites(true);
    apiRequest<{
      emails: string[];
    }>('POST', '/onboarding/invite-friends/', { emails: inviteList }, { apiVersion: 'v2' }).finally(
      () => {
        setSendingInvites(false);
        proceedToNextStep();
      },
    );
  };

  return (
    <Panel>
      <CopySection>
        <h2>Invite your friends</h2>
        <p>Clay is much better with friends — send them an invite!</p>
        <FinishStepButton loading={sendingInvites} onClick={finishStep}>
          Continue
        </FinishStepButton>
      </CopySection>
      <EmailList inviteList={inviteList} setInviteList={setInviteList} />
    </Panel>
  );
}
